<script setup>
import stockMenus from './mobileMenus.json'
import mobileAccountMenus from './mobileAccountMenus.json'
import additionalMenus from './additionalMenus.json'
import ga4 from '#common/utils/ga4'
import { pushEvent } from '#common/utils/user'
import { mobileOccasionsMenu } from '#wa/utils/metadata'

const { $store } = useNuxtApp()
const $route = useRoute()
const layout = useLayout()
const router = useRouter()

const selectedMenus = ref([])
const transitionDirection = ref('left')
const animateKeyCounter = ref(0)
const showMenuActions = ref(true)
const shownOnce = ref(false)
const metadata = useMetadata()

const menus = computed(() => {
  const menus = _.cloneDeep(stockMenus)
  menus.items.splice(0, 0, mobileAccountMenus)
  menus.items.splice(
    7,
    0,
    mobileOccasionsMenu(
      metadata.occasions,
      metadata.constance.EP1_ENABLE === 'True',
    ),
  )
  menus.items.push(...additionalMenus.items)

  return menus
})
const activeMenus = computed(() => {
  return [...selectedMenus.value, menus.value]
})
const currentMenu = computed(() => {
  return activeMenus.value[0]
})

const currentMenuTitle = computed(() => {
  if (currentMenu.value.text === 'root') {
    return 'Menu'
  }
  return currentMenu.value.text
})

const transitionName = computed(() => {
  if (
    currentMenu.value &&
    currentMenu.value.text === 'root' &&
    !shownOnce.value
  ) {
    return 'mobile-menu'
  }
  return `slide-${transitionDirection.value}`
})
watch(
  () => layout.showMobileMenu,
  (newValue, oldValue) => {
    if (!newValue && oldValue) {
      selectedMenus.value = []
      document.querySelector('body').classList.remove('noscroll')
    } else {
      document.querySelector('body').classList.add('noscroll')
    }
  },
)
watch(
  $route,
  () => {
    nextTick(() => {
      layout.showMobileMenu = false
    })
  },
  { immediate: false },
)

function refreshAnimate(direction) {
  transitionDirection.value = direction
  animateKeyCounter.value += 1
}
function setActiveMenu(menu) {
  selectedMenus.value = [menu, ...selectedMenus.value]
  refreshAnimate('left')
}
function menuBack() {
  selectedMenus.value = selectedMenus.value.slice(1)
  refreshAnimate('right')
}
function menuClose() {
  layout.showMobileMenu = false
}
function linkClicked(item) {
  let parentName =
    currentMenu.value.text !== 'root' ? currentMenu.value.text : ''
  // The items under "Occasion" have categories
  if (item.category) parentName = parentName + ' / ' + item.category
  pushEvent(
    {
      metric_name: 'Global Nav Click',
      url_path: $route.fullPath,
      extra_data: {
        menu_name: item.text,
        parent_menu_name: parentName,
        target_url: item.url,
        is_mobile_web: true,
      },
    },
    $store,
  )
  ga4.track('global_nav_click', {
    from: $route.fullPath,
    name: item.text,
    parent_name: parentName,
    path: item.url,
  })
  router.push(item.url)
}
function handleBeforeEnter() {
  showMenuActions.value = false
}
function handleAfterEnter() {
  showMenuActions.value = true
}
function handleBeforeLeave(el) {
  showMenuActions.value = false
  // The transition prevents v-show and v-if from taking effect right away
  // this manual style change will hide the menu actions immediately as the menu starts to close
  const menu = el.querySelector('.mobile-menu-layout')
  if (menu) {
    menu.style.display = 'none'
  }
}
</script>
<template>
  <div>
    <div
      v-if="layout.showMobileMenu"
      class="site-cover"
      @click="menuClose"
    ></div>
    <transition
      :name="transitionName"
      @before-enter="handleBeforeEnter"
      @after-enter="handleAfterEnter"
      @before-leave="handleBeforeLeave"
    >
      <nav
        v-if="layout.showMobileMenu"
        :key="animateKeyCounter"
        class="mobile-site-navigation"
        aria-label="Main Menu"
        @keydown.esc="layout.showMobileMenu = false"
      >
        <FocusLoop :enabled="true">
          <MobileMenuActions
            v-if="layout.showMobileMenu"
            v-show="showMenuActions"
            :show-active-section="activeMenus.length > 1"
            :show-back="activeMenus.length > 1"
            :title="currentMenuTitle"
            @back="menuBack"
            @close="menuClose"
          />
          <MobileMenu
            :menu="currentMenu"
            @link-clicked="linkClicked"
            @setActiveMenu="setActiveMenu"
          />
        </FocusLoop>
      </nav>
    </transition>
  </div>
</template>

<style lang="scss">
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: left 0.5s ease;
}
.mobile-menu-enter,
.mobile-menu-leave-to {
  left: -100vw !important;
}
.mobile-menu-leave,
.mobile-menu-enter-to {
  left: 0 !important;
}

.slide-left-enter,
.slide-right-enter {
  position: absolute; // Position absolutely to not affect other elements
  width: 100%; // Maintain full width
  height: 100%;
  scale: 1;
}

.slide-left-enter-active,
.slide-left-leave-active {
  transition:
    left 0.5s ease,
    right 0.5s ease;
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition:
    left 0.5s ease,
    right 0.5s ease;
}

.slide-left-enter {
  left: -100%;
  right: 100%;
}
.slide-right-enter {
  left: 100%; // Starts from completely right
  right: -100%; // Complement to left
}
</style>
